import { CATALOG_TYPE_PRIMA_KIDS } from './Service.js';
import {
  SERVICE_OPERATION_TYPE_ACTIVATION,
  SERVICE_OPERATION_TYPE_DEACTIVATION,
  SERVICE_UUIDS,
} from '../constants.js';

/**
 * @property {Service[]} services
 * @property {Map<string,Category>} categoriesMap
 * @property {string} subscriptionId
 * @property {string} basketId
 * @property {Tariff} tariff
 * @property {Price} initialBasketPrice
 * @property {Price} updatedBasketPrice
 * @property {number} minimalMonthlyFee
 * @property {number} currentMonthlyFee
 * @property {number} monthlyFeeDiff
 * @property {boolean} isReadOnly
 * @property {boolean} isSuspended
 * @property {boolean} hasInvoiceOverdue
 * @property {boolean} isChangingTariff
 */
export class Basket {
  /**
   * @param {string} subscriptionId
   * @param {string} basketId
   * @param {Tariff} tariff
   * @param {Price} initialBasketPrice
   * @param {Price} updatedBasketPrice
   * @param {number} minimalMonthlyFee
   * @param {number} currentMonthlyFee
   * @param {number} monthlyFeeDiff
   * @param {boolean} isReadOnly
   * @param {boolean} isSuspended
   * @param {boolean} hasInvoiceOverdue
   * @param {boolean} isChangingTariff
   */
  constructor({
    subscriptionId,
    basketId,
    tariff,
    initialBasketPrice,
    updatedBasketPrice,
    minimalMonthlyFee,
    currentMonthlyFee,
    monthlyFeeDiff,
    isReadOnly,
    isSuspended,
    hasInvoiceOverdue,
    isChangingTariff,
  }) {
    this.services = [];
    this.categoriesMap = new Map();
    this.subscriptionId = subscriptionId;
    this.basketId = basketId;
    this.tariff = tariff;
    this.initialBasketPrice = initialBasketPrice;
    this.updatedBasketPrice = updatedBasketPrice;
    this.minimalMonthlyFee = minimalMonthlyFee;
    this.currentMonthlyFee = currentMonthlyFee;
    this.monthlyFeeDiff = monthlyFeeDiff;
    this.isReadOnly = isReadOnly;
    this.isSuspended = isSuspended;
    this.hasInvoiceOverdue = hasInvoiceOverdue;
    this.isChangingTariff = isChangingTariff;
    this.isIncomplete = false;
  }

  /**
   * @returns {Category[]}
   */
  get categories() {
    return Array.from(this.categoriesMap.values()).sort((a, b) => a.order - b.order);
  }

  /**
   * @returns {boolean}
   */
  get hasUnprocessedRequests() {
    return this.services.some(service => service.hasUnprocessedRequest);
  }

  /**
   *
   * @param {number} uuid
   * @returns {boolean}
   */
  hasService(uuid) {
    return this.services.some(service => service.uuid === uuid);
  }

  /**
   *
   * @param {number} uuid
   * @returns {?Service}
   */
  getServiceByUuid(uuid) {
    const service = this.services.find(s => s.uuid === uuid);
    return service || null;
  }

  /**
   *
   * @param {Service => boolean} filter
   * @return {Service[]}
   */
  getFilteredServices(filter) {
    return this.services.filter(filter);
  }

  /**
   *
   * @return {Service[]}
   */
  getActiveServices() {
    return this.getFilteredServices(service => service.isActive);
  }

  /**
   *
   * @return {Service[]}
   */
  getVisibleActiveServices() {
    return this.getFilteredServices(service => service.isActive && service.isVisible);
  }

  /**
   * @return {Service[]}
   */
  getVisibleActiveServicesForSummary() {
    return this.getFilteredServices(service => service.isActive && service.isVisibleInSummary);
  }

  /**
   *
   * @return {Service[]}
   */
  getVisibleEligibleServices() {
    return this.getFilteredServices(
      service =>
        service.isEligible &&
        service.isVisible &&
        (!this.isChangingTariff || service.canBeModifiedDuringTariffChange),
    );
  }

  /**
   *
   * @return {Map<Category, Service>}
   */
  getVisibleEligibleServicesByCategories() {
    return this.getFilteredServicesByCategory(
      service =>
        service.isEligible &&
        service.isVisible &&
        (!this.isChangingTariff || service.canBeModifiedDuringTariffChange),
    );
  }

  /**
   *
   * @return {Service[]}
   */
  getVisibleRecommendedServices() {
    return this.getFilteredServices(
      service =>
        service.isRecommended &&
        !service.isActive &&
        service.isEligible &&
        service.isVisible &&
        (!this.isChangingTariff || service.canBeModifiedDuringTariffChange),
    );
  }

  /**
   * Returns filtered list by categories for subsidy services screen.
   *
   * @return {Map<string, Service>}
   */
  getSubsidyServices() {
    return this.getFilteredServicesByCategory(
      service =>
        service.canBeDeactivated &&
        !service.isDeactivating &&
        !service.isActivating &&
        service.changeDate === null &&
        service.price.hasCycle &&
        service.price.cycle > 0 &&
        (service.isActive ||
          (service.isSubsidy && service.isEligible) ||
          (!service.isActive && service.isInitiallyActive)),
    );
  }

  /**
   * @return {Service[]}
   */
  get addedInLastUpdate() {
    return this.services.filter(service => service.isAddedInLastUpdate);
  }

  /**
   * @return {Service[]}
   */
  get removedInLastUpdate() {
    return this.services.filter(service => service.isRemovedInLastUpdate);
  }

  /**
   * Checks, if last update triggered conflict of services.
   *
   * @param {('activate'|'deactivate'|'update')} operationType
   * @return {Service[]}
   */
  getServicesInConflict(operationType) {
    if (
      operationType === SERVICE_OPERATION_TYPE_ACTIVATION &&
      this.addedInLastUpdate.length === 1 &&
      this.removedInLastUpdate.length >= 1
    ) {
      return this.removedInLastUpdate;
    }

    if (
      operationType === SERVICE_OPERATION_TYPE_DEACTIVATION &&
      this.removedInLastUpdate.length === 1 &&
      this.addedInLastUpdate.length >= 1
    ) {
      return this.addedInLastUpdate;
    }

    return [];
  }

  /**
   * Returns list of services parsed from Prima Kids catalog.
   *
   * @return {Map<string, Service>}
   */
  getVisiblePrimaKidsProfileServices() {
    return this.getFilteredServicesByCategory(
      service => service.catalogType === CATALOG_TYPE_PRIMA_KIDS && service.isVisible,
    );
  }

  /**
   * Get the migration service for the latest Prima profile.
   * Currently PRIMA_DATA.
   *
   * @return {?Service}
   */
  get primaMigrationService() {
    return this.getServiceByUuid(SERVICE_UUIDS.PRIMA_MIGRATION_SERVICE.PRIMA_DATA);
  }

  /**
   * Return if ID/RC verification is required before basket submit.
   *
   * @return {boolean}
   */
  isIdRcVerificationRequired() {
    return this.services.some(service => service.isChanged && service.isIdRcVerificationRequired);
  }

  /**
   * Returns estimated sum for monthly fee for loaded Prima Kids profile services.
   *
   * @returns {number}
   */
  getPrimaKidsProfileMonthlyFee() {
    return this.getFilteredServices(
      service =>
        service.catalogType === CATALOG_TYPE_PRIMA_KIDS && service.isVisible && service.isActive,
    )
      .map(service => service.price.cycle)
      .reduce((sum, price) => sum + price, 0.0);
  }

  /**
   * Empty categories are not returned.
   *
   * @param {Service => boolean} filter
   * @return {Map<string, Service>}
   */
  getFilteredServicesByCategory(filter) {
    const servicesByCategory = new Map();
    const filteredServices = this.getFilteredServices(filter);

    this.categories.forEach(category => {
      const filteredServicesByCategory = filteredServices.filter(
        service => service.category === category.name,
      );
      if (filteredServicesByCategory.length) {
        servicesByCategory.set(category, filteredServicesByCategory);
      }
    });
    return servicesByCategory;
  }

  /**
   * Returns expected monthly fee difference change after submitting the basket.
   * This fee is based on items in basket and can be different from real fee on invoice.
   * Fee represents cycle price difference only.
   *
   * @returns {number}
   */
  get invoiceMonthlyDiff() {
    if (
      !this.updatedBasketPrice.exists ||
      !this.updatedBasketPrice.hasCycle ||
      this.updatedBasketPrice.isEqual(this.initialBasketPrice)
    ) {
      return 0;
    }

    return this.updatedBasketPrice.cycle - this.initialBasketPrice.cycle;
  }

  /**
   * Returns sum of prices of all services in basket added for activation.
   * *Used only for analytic purposes*
   *
   * @returns {number}
   */
  get total() {
    return this.getFilteredServices(service => service.isAdded).reduce(
      (sum, service) => sum + service.price.cycle + service.price.purchase,
      0,
    );
  }

  /**
   * Returns sum of discounts of all services in basket added for activation.
   * *Used only for analytic purposes*
   *
   * @returns {number}
   */
  get discount() {
    return this.getFilteredServices(
      service => service.isAdded && service.originalPrice.exists,
    ).reduce((sum, service) => {
      if (service.originalPrice.exists && !service.originalPrice.isEqual(service.price)) {
        return (
          sum +
          service.originalPrice.cycle -
          service.price.cycle +
          service.originalPrice.purchase -
          service.price.purchase
        );
      }
      return sum;
    }, 0);
  }
}
